import { useCallback, useEffect, useState } from "react";
import { Loader2, Download, Upload, AlertTriangle, CheckCircle2, FileSpreadsheet } from "lucide-react";
import { AdminShell } from "../components/AdminShell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { api, apiError, API } from "../lib/api";
import { toast } from "sonner";

const ImportContent = () => {
  const [file, setFile] = useState(null);
  const [validating, setValidating] = useState(false);
  const [preview, setPreview] = useState(null);
  const [decisions, setDecisions] = useState({});
  const [batchName, setBatchName] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState(null);
  const [batches, setBatches] = useState([]);

  const loadBatches = useCallback(() => {
    api.get("/admin/import/batches").then((r) => setBatches(r.data.batches || [])).catch(() => {});
  }, []);
  useEffect(() => { loadBatches(); }, [loadBatches]);

  const downloadTemplate = async () => {
    try {
      const res = await fetch(`${API}/admin/import/template`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("hz_admin_token")}` },
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "plantilla_importacion_horizonte2026.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("No se pudo descargar la plantilla.");
    }
  };

  const validate = async () => {
    if (!file || validating) return;
    setValidating(true);
    setPreview(null);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/import/validate", fd);
      setPreview(data);
      const d = {};
      data.items.forEach((i) => { if (i.duplicate_of) d[i.index] = "skip"; });
      setDecisions(d);
      setBatchName(`Lote ${new Date().toLocaleDateString("es-MX")} ${file.name}`);
    } catch (err) {
      toast.error(apiError(err, "No se pudo validar el archivo."));
    } finally {
      setValidating(false);
    }
  };

  const confirm = async () => {
    if (!preview || confirming) return;
    setConfirming(true);
    try {
      const { data } = await api.post("/admin/import/confirm", {
        batch_name: batchName,
        source_file_name: preview.file_name,
        rows: preview.items.map((i) => i.raw),
        decisions,
      });
      setResult(data);
      setPreview(null);
      setFile(null);
      toast.success("Importación completada");
      loadBatches();
    } catch (err) {
      toast.error(apiError(err, "No se pudo confirmar la importación."));
    } finally {
      setConfirming(false);
    }
  };

  const duplicatesPending = preview?.items.filter((i) => i.duplicate_of && decisions[i.index] !== "skip" && decisions[i.index] !== "create") || [];

  return (
    <div className="space-y-8" data-testid="import-module">
      <section className="rounded-2xl border border-hz-gray/40 bg-white p-6">
        <h2 className="flex items-center gap-2 text-lg font-bold text-hz-navy"><FileSpreadsheet className="h-5 w-5 text-hz-cyan" /> 1. Plantilla</h2>
        <p className="mt-2 text-sm text-hz-ink/70">
          Descarga la plantilla CSV (puedes abrirla en Excel y guardarla como .xlsx). Columnas: group_type (individual/family/group), group_name, datos de contacto, datos del adulto, delegación/sector/célula (por nombre), accesos, niños opcionales y estado de pago.
        </p>
        <Button onClick={downloadTemplate} variant="outline" data-testid="import-template-btn" className="mt-3 font-bold text-hz-navy">
          <Download className="mr-2 h-4 w-4" /> Descargar plantilla
        </Button>
      </section>

      <section className="rounded-2xl border border-hz-gray/40 bg-white p-6">
        <h2 className="text-lg font-bold text-hz-navy">2. Subir y validar archivo</h2>
        <div className="mt-3 flex flex-col gap-2 sm:flex-row">
          <Input type="file" accept=".csv,.xlsx" data-testid="import-file-input" onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); }} />
          <Button onClick={validate} disabled={!file || validating} data-testid="import-validate-btn" className="bg-hz-navy font-bold text-white disabled:opacity-60">
            {validating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />} Validar
          </Button>
        </div>
      </section>

      {preview && (
        <section className="rounded-2xl border border-hz-gray/40 bg-white p-6" data-testid="import-preview">
          <h2 className="text-lg font-bold text-hz-navy">3. Vista previa</h2>
          <p className="mt-1 text-sm text-hz-ink/70" data-testid="import-preview-summary">
            {preview.total_rows} filas · {preview.valid_rows} válidas · <span className="font-bold text-hz-error">{preview.rows_with_errors} con errores</span> · <span className="font-bold text-hz-warning">{preview.duplicates} posibles duplicados</span>
          </p>
          <div className="mt-4 overflow-x-auto rounded-xl border border-hz-gray/40">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-bold text-hz-navy">#</TableHead>
                  <TableHead className="font-bold text-hz-navy">Grupo</TableHead>
                  <TableHead className="font-bold text-hz-navy">Adulto</TableHead>
                  <TableHead className="font-bold text-hz-navy">Pago</TableHead>
                  <TableHead className="font-bold text-hz-navy">Estado</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {preview.items.map((i) => (
                  <TableRow key={i.index} data-testid={`import-row-${i.index}`}>
                    <TableCell className="text-xs">{i.index + 1}</TableCell>
                    <TableCell className="text-xs">{i.data.group_name || i.data.contact_name}<br />{i.data.group_type}</TableCell>
                    <TableCell className="text-xs font-semibold">{i.data.adult_name}<br /><span className="font-normal text-hz-ink/60">{i.data.adult_email || i.data.adult_phone || "—"}</span></TableCell>
                    <TableCell className="text-xs">{i.data.payment_status}{i.data.payment_amount ? ` · $${i.data.payment_amount}` : ""}</TableCell>
                    <TableCell className="text-xs">
                      {i.errors.length > 0 ? (
                        <span className="font-semibold text-hz-error" data-testid={`import-row-errors-${i.index}`}>{i.errors.join(" · ")}</span>
                      ) : i.duplicate_of ? (
                        <select value={decisions[i.index] || ""} onChange={(e) => setDecisions({ ...decisions, [i.index]: e.target.value })} data-testid={`import-row-decision-${i.index}`}
                          className="h-8 rounded-md border border-hz-warning/50 bg-hz-warning/5 px-2 text-xs font-semibold">
                          <option value="">Posible duplicado de {i.duplicate_of}: decidir…</option>
                          <option value="skip">Omitir fila</option>
                          <option value="create">Crear de todas formas (persona diferente)</option>
                        </select>
                      ) : (
                        <span className="font-semibold text-hz-success">Válida
                          {i.data.child_name && (
                            <span className="block font-semibold text-hz-warning" data-testid={`import-row-nophoto-${i.index}`}>Niño sin foto: cuidado bloqueado hasta validarla</span>
                          )}
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="mt-4 space-y-3">
            <div>
              <Label className="text-sm font-semibold text-hz-ink">Nombre del lote *</Label>
              <Input value={batchName} onChange={(e) => setBatchName(e.target.value)} data-testid="import-batch-name" className="mt-1" />
            </div>
            {duplicatesPending.length > 0 && (
              <p className="flex items-center gap-2 text-sm font-semibold text-hz-warning" data-testid="import-duplicates-warning">
                <AlertTriangle className="h-4 w-4" /> Hay {duplicatesPending.length} posible(s) duplicado(s) sin decisión: esas filas no se importarán.
              </p>
            )}
            <Button onClick={confirm} disabled={confirming || batchName.trim().length < 3 || preview.valid_rows === 0} data-testid="import-confirm-btn"
              className="bg-hz-cyan font-bold text-white hover:bg-hz-blue disabled:opacity-60">
              {confirming ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}
              Confirmar importación
            </Button>
          </div>
        </section>
      )}

      {result && (
        <section className="rounded-2xl border border-hz-success/30 bg-hz-success/5 p-6" data-testid="import-result">
          <h2 className="flex items-center gap-2 text-lg font-bold text-hz-success"><CheckCircle2 className="h-5 w-5" /> Importación completada</h2>
          <p className="mt-2 text-sm text-hz-ink">
            Lote <b>{result.batch.batch_name}</b>: {result.batch.successful_rows} adulto(s) importado(s), {result.batch.groups_created} grupo(s) creado(s), {result.batch.failed_rows} fila(s) con error, {result.batch.skipped_rows} omitida(s).
            {result.batch.children_without_photo > 0 && (
              <span className="mt-1 block font-semibold text-hz-warning" data-testid="import-children-no-photo">
                {result.batch.children_without_photo} niño(s) importado(s) sin fotografía: su acceso a cuidado infantil queda bloqueado hasta que un responsable valide su foto.
              </span>
            )}
          </p>
          <ul className="mt-3 max-h-48 space-y-1 overflow-y-auto text-xs text-hz-ink/80">
            {result.results.map((r) => (
              <li key={r.index} data-testid={`import-result-row-${r.index}`}>
                Fila {r.index + 1}: {r.status === "created" ? `Creado (${r.folio} · ${r.group_folio})` : r.status === "skipped_duplicate" ? `Omitida (duplicado de ${r.folio})` : `Error: ${(r.errors || []).join(" · ")}`}
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="rounded-2xl border border-hz-gray/40 bg-white p-6" data-testid="import-batches">
        <h2 className="text-lg font-bold text-hz-navy">Lotes anteriores</h2>
        {batches.length === 0 ? (
          <p className="mt-2 text-sm text-hz-ink/60">Aún no hay importaciones.</p>
        ) : (
          <ul className="mt-3 divide-y divide-hz-gray/30">
            {batches.map((b) => (
              <li key={b.id} className="py-2.5 text-sm" data-testid={`import-batch-${b.id}`}>
                <span className="font-bold text-hz-navy">{b.batch_name}</span>
                <span className="text-hz-ink/60"> — {b.successful_rows}/{b.total_rows} exitosas · {b.groups_created} grupo(s) · {b.imported_by_name || ""} · {new Date(b.created_at).toLocaleString("es-MX")}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
};

const AdminImportaciones = () => (
  <AdminShell roles={["admin"]} title="Importaciones" testId="admin-importaciones"
    subtitle="Carga masiva de registros con pago confirmado. Los QR se generan solo para registros con pago confirmado o cortesía.">
    {() => <ImportContent />}
  </AdminShell>
);

export default AdminImportaciones;
